
import React, { useEffect } from "react";
import { AIStaffMember } from "@/lib/types";
import QuickStartCard from "./onboarding/QuickStartCard";
import AssistantCard from "./onboarding/AssistantCard";
import { useCreateAIStaff } from "./hooks/useCreateAIStaff";
import { useQueryClient } from "@tanstack/react-query";

interface OnboardingStageProps {
  aiStaff: AIStaffMember | null;
  onComplete: () => void;
}

const OnboardingStage: React.FC<OnboardingStageProps> = ({ 
  aiStaff, 
  onComplete
}) => {
  const queryClient = useQueryClient();
  
  // Use the hook to handle AI staff creation
  useCreateAIStaff({ aiStaff });
  
  // When aiStaff changes (i.e., a new staff is created), invalidate queries
  useEffect(() => {
    if (aiStaff) {
      // Invalidate all staff-related queries to trigger refetching
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['allStaff'] });
      
      // Also invalidate dashboard data which displays staff
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  }, [aiStaff, queryClient]);

  return (
    <div className="space-y-6 animate-fade-in">
      <h3 className="text-2xl font-bold">Get Started with Your AI Assistant</h3>
      <p className="text-muted-foreground">
        Your AI assistant is ready to help grow your business. Here's how to get started.
      </p>
      
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <QuickStartCard />
        <AssistantCard aiStaff={aiStaff} onComplete={onComplete} />
      </div>
    </div>
  );
};

export default OnboardingStage;
